import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { ENGRAVING_MAX_LEN } from "../../utils/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Trash2, Plus, Package, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function PrintOrderFlow() {
  const { recordId } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [products, setProducts] = useState([]);
  const [charges, setCharges] = useState({ engraving_charge: 0, shipping_options: [] });
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Add-item dialog
  const [picked, setPicked] = useState(null);
  const [eng1, setEng1] = useState("");
  const [eng2, setEng2] = useState("");
  const [qty, setQty] = useState(1);

  const [addr, setAddr] = useState({ name: "", phone: "", line1: "", city: "", state: "", postal_code: "", country: "US" });
  const [contactEmail, setContactEmail] = useState("");

  // Product search + collection filter (Infinity Touch only). Multiple
  // collections may be selected at once.
  const [collections, setCollections] = useState([]);
  const [selectedCols, setSelectedCols] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [debKeyword, setDebKeyword] = useState("");
  const toggleCol = (id) => setSelectedCols((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // One-time load: record, charges, collections, and FH address prefill.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [recRes, chRes, colRes, storeRes] = await Promise.all([
          axios.get(`${API}/memorial-records/${recordId}`).catch(() => ({ data: null })),
          axios.get(`${API}/reseller/it-charges`),
          axios.get(`${API}/reseller/storefront/collections?channel=infinity`).catch(() => ({ data: [] })),
          axios.get(`${API}/stores/my`).catch(() => ({ data: null })),
        ]);
        setRecord(recRes.data);
        setCharges(chRes.data || {});
        setCollections(colRes.data || []);
        // Prefill shipping with the funeral home's own address (read-only).
        const st = storeRes.data;
        if (st?.business_address?.address) {
          setAddr({
            name: st.business_name || "",
            phone: st.contact_phone || "",
            line1: st.business_address.address || "",
            city: st.business_address.city || "",
            state: st.business_address.state || "",
            postal_code: st.business_address.zipcode || "",
            country: st.business_address.country || "US",
          });
        }
        if (st?.contact_email) setContactEmail(st.contact_email);
      } catch (e) {
        toast.error("Failed to load ordering data");
      } finally {
        setLoading(false);
      }
    })();
  }, [recordId]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setDebKeyword(keyword.trim()), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  // Re-fetch products (IT-only) when search / collections change. Multiple
  // selected collections → union of their products (deduped by product_id).
  useEffect(() => {
    (async () => {
      const build = (collectionId) => {
        const p = new URLSearchParams({ channel: "infinity", limit: "100" });
        if (debKeyword) p.set("keyword", debKeyword);
        if (collectionId) p.set("collection_id", collectionId);
        return p.toString();
      };
      try {
        let list = [];
        if (selectedCols.length === 0) {
          const res = await axios.get(`${API}/reseller/storefront/products?${build()}`);
          list = res.data?.products || [];
        } else {
          const results = await Promise.all(
            selectedCols.map((id) =>
              axios.get(`${API}/reseller/storefront/products?${build(id)}`).then((r) => r.data?.products || []).catch(() => [])),
          );
          const seen = new Set();
          list = results.flat().filter((p) => (seen.has(p.product_id) ? false : seen.add(p.product_id)));
        }
        setProducts(list);
      } catch (e) {
        /* keep previous list */
      }
    })();
  }, [debKeyword, selectedCols]);

  const openAdd = (p) => { setPicked(p); setEng1(""); setEng2(""); setQty(1); };

  const addToCart = () => {
    const lines = [eng1, eng2].map((l) => l.trim()).filter(Boolean);
    setCart((c) => [...c, {
      key: `${picked.product_id}_${Date.now()}`,
      product: picked,
      quantity: Math.max(1, qty),
      engraving_lines: lines,
    }]);
    setPicked(null);
  };

  const removeLine = (key) => setCart((c) => c.filter((i) => i.key !== key));

  // FH orders are billed at base price with no shipping charge (TR-7); the
  // multiplied/customer price is never shown to the funeral home.
  const totals = useMemo(() => {
    let productSubtotal = 0, engraving = 0;
    for (const it of cart) {
      const price = it.product.base_price ?? it.product.display_price ?? 0;
      productSubtotal += price * it.quantity;
      engraving += it.engraving_lines.length * (charges.engraving_charge || 0) * it.quantity;
    }
    const subtotal = productSubtotal + engraving;
    return { productSubtotal, engraving, total: subtotal };
  }, [cart, charges]);

  const submit = async () => {
    if (cart.length === 0) return toast.error("Add at least one product");
    if (!addr.line1 || !addr.city || !addr.state || !addr.postal_code) return toast.error("Enter a shipping address");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reseller/orders/place`, {
        memorial_record_id: recordId,
        channel: "infinity",
        contact_email: contactEmail,
        customer_name: addr.name,
        deceased_name: record?.subject ? [record.subject.first_name, record.subject.last_name].filter(Boolean).join(" ") : undefined,
        shipping_address: addr,
        items: cart.map((it) => ({
          product_id: it.product.product_id,
          channel: it.product.channel || "infinity",
          quantity: it.quantity,
          color: it.product.color,
          engraving_lines: it.engraving_lines,
        })),
      });
      toast.success(`Order ${res.data.order_number} placed (PIN ${res.data.customer_pin})`);
      navigate("/reseller/print-orders");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-slate-400">Loading…</div>;

  const subjectName = record?.subject ? [record.subject.first_name, record.subject.last_name].filter(Boolean).join(" ") : "";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="print-order-flow">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/reseller/print-records")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Create Print Order</h2>
          <p className="text-slate-500">{record ? `Case ${record.case_number} · ${subjectName}` : recordId} · No online payment</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-[calc(100vh-12rem)]">
        {/* Collections (left) — own scroll */}
        <aside className="lg:col-span-2 lg:h-full lg:overflow-y-auto">
          <div className="card-elevated p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800 text-sm">Collections</h3>
              {selectedCols.length > 0 && (
                <button onClick={() => setSelectedCols([])} className="text-[11px] text-indigo-600 hover:underline">Clear</button>
              )}
            </div>
            <div className="space-y-0.5">
              {collections.length === 0 ? (
                <p className="text-xs text-slate-400">No collections</p>
              ) : collections.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selectedCols.includes(c.id)} onChange={() => toggleCol(c.id)} />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        {/* Products (middle) — static search + own-scrolling grid */}
        <div className="lg:col-span-7 lg:h-full flex flex-col min-h-0">
          <div className="relative shrink-0 pb-3">
            <Input placeholder="Search Infinity Touch products" value={keyword} onChange={(e) => setKeyword(e.target.value)} className="pr-9" />
            {keyword && (
              <button onClick={() => setKeyword("")} className="absolute right-2 top-[18px] -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear search">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 flex-1 overflow-y-auto pr-1 content-start">
            {products.map((p) => (
              <div key={p.product_id} className="card-elevated p-2 flex flex-col">
                <div className="h-24 bg-slate-50 rounded-lg flex items-center justify-center overflow-hidden mb-2">
                  {p.image_url ? <img src={getImageUrl(p.image_url)} alt={p.name} className="h-full object-contain" /> : <Package className="w-7 h-7 text-slate-300" />}
                </div>
                <p className="text-xs font-medium text-slate-900 line-clamp-2 min-h-[2rem]">{p.name}</p>
                <p className="text-sm text-indigo-700 font-semibold mt-1">{money(p.base_price ?? p.display_price)}</p>
                <Button size="sm" className="mt-2 rounded-lg h-8 text-xs" onClick={() => openAdd(p)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
            ))}
            {products.length === 0 && <p className="text-slate-400 text-sm col-span-full">No products found.</p>}
          </div>
        </div>

        {/* Cart + checkout (right) — own scroll */}
        <aside className="lg:col-span-3 space-y-4 lg:h-full lg:overflow-y-auto pr-1">
          <div className="card-elevated p-4">
            <h3 className="font-semibold text-slate-800 mb-3">Order Summary</h3>
            {cart.length === 0 ? (
              <p className="text-sm text-slate-400">No items yet.</p>
            ) : (
              <div className="space-y-3">
                {cart.map((it) => (
                  <div key={it.key} className="flex items-start gap-2 border-b pb-2">
                    <div className="w-10 h-10 rounded border bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                      {it.product.image_url ? <img src={getImageUrl(it.product.image_url)} alt={it.product.name} className="w-full h-full object-contain" /> : <Package className="w-4 h-4 text-slate-300" />}
                    </div>
                    <div className="text-sm flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{it.product.name} × {it.quantity}</p>
                      {it.engraving_lines.length > 0 && <p className="text-xs text-slate-500 truncate">Engraving: {it.engraving_lines.join(" / ")}</p>}
                    </div>
                    <button onClick={() => removeLine(it.key)} className="text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <Row label="Products" value={money(totals.productSubtotal)} />
                <Row label="Engraving" value={money(totals.engraving)} />
                <div className="border-t pt-2"><Row label="Grand Total" value={money(totals.total)} bold /></div>
              </div>
            )}
          </div>

          <div className="card-elevated p-4 space-y-2">
            <h3 className="font-semibold text-slate-800 mb-1">Shipping address</h3>
            <p className="text-[11px] text-slate-400">Ships to your funeral home on file. No shipping charge for funeral-home orders.</p>
            {/* FH address is read-only — taken from the store profile. */}
            <Input placeholder="Recipient name" value={addr.name} disabled />
            <Input placeholder="Phone" value={addr.phone} disabled />
            <Input placeholder="Contact email (for family link)" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            <Input placeholder="Address line" value={addr.line1} disabled />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="City" value={addr.city} disabled />
              <Input placeholder="State" value={addr.state} disabled />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="ZIP" value={addr.postal_code} disabled />
              <Input placeholder="Country" value={addr.country} disabled />
            </div>
          </div>

          <Button className="w-full rounded-xl" disabled={submitting} onClick={submit}>
            {submitting ? "Placing…" : "Submit Order (No Payment)"}
          </Button>
        </aside>
      </div>

      {/* Add item dialog */}
      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{picked?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-indigo-700 font-semibold">{money(picked?.base_price ?? picked?.display_price)}</p>
            <div className="space-y-1">
              <Label className="text-xs">Engraving line 1 (optional)</Label>
              <Input maxLength={ENGRAVING_MAX_LEN} value={eng1} onChange={(e) => setEng1(e.target.value)} />
              <p className="text-[11px] text-slate-400 text-right">{eng1.length} / {ENGRAVING_MAX_LEN}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Engraving line 2 (optional)</Label>
              <Input maxLength={ENGRAVING_MAX_LEN} value={eng2} onChange={(e) => setEng2(e.target.value)} />
              <p className="text-[11px] text-slate-400 text-right">{eng2.length} / {ENGRAVING_MAX_LEN}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quantity</Label>
              <Input type="number" min={1} value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 1)} />
            </div>
            <p className="text-[11px] text-slate-400">Engraving is {money(charges.engraving_charge)} per line.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicked(null)}>Cancel</Button>
            <Button onClick={addToCart}>Add to Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-500"}>{label}</span>
      <span className={bold ? "font-bold text-slate-900" : "font-medium text-slate-800"}>{value}</span>
    </div>
  );
}
